import React from 'react';
import {
    PieChart,
    Pie,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Cell,
    TooltipProps
} from 'recharts';
import { COLORS } from "../"; // Assuming COLORS is exported from the parent index
import { DataAttributes, DataStackedAttributes } from '../types';
import { GraphTooltip } from '../shared/GraphTooltip';

interface PieGraphProps {
    data: DataAttributes[];
}

interface PieGraphStackedProps {
    data: DataStackedAttributes[];
    // Potential future props: innerRadius, outerRadius, showLegend, etc.
}

// Simple PieGraph wrapper
export const PieGraph: React.FC<PieGraphProps> = ({
    data,
}) => {
    // Transform simple data to the "stacked" format for consistency
    const stackedData: DataStackedAttributes[] = data.map(item => ({
        x: item.x, // Keep x for potential future use or tooltip context
        y: [item.y],
        label: [item.label || String(item.x)] // Use label or fallback to x
    }));

    return <PieGraphStacked data={stackedData} />;
};

// "Stacked" PieGraph implementation
export const PieGraphStacked: React.FC<PieGraphStackedProps> = ({
    data,
}) => {
    // Transform DataStackedAttributes[] into format for Recharts Pie [{ name, value }, ...]
    // Each y/label pair across all input items becomes a slice
    const pieData = data.flatMap(item =>
        item.y.map((value, index) => ({
            name: item.label[index] || `Slice ${index + 1}`, // Use label as slice name
            value: value,
            // Optional: include original 'x' if needed for tooltip context
            originalX: item.x
        }))
    );

    // Filter out slices with zero or negative value as they cause issues in recharts pie
    const validPieData = pieData.filter(d => d.value > 0);

    // Custom extractors for Pie Chart data structure
    const pieTitleExtractor = () => null; // Pie chart usually doesn't need a title based on hover

    const pieItemsExtractor = (payload: any[]) => {
        if (!payload || payload.length === 0) return [];
        // For Pie charts, payload is usually an array with one item
        const slicePayload = payload[0];
        if (!slicePayload || !slicePayload.payload) return [];

        const sliceData = slicePayload.payload; // Data is nested inside payload[0].payload
        const color = slicePayload.color || slicePayload.fill; // Get color from Recharts payload

        return [{
            label: sliceData.name, // Use the 'name' field from pie data
            value: sliceData.value, // Use the 'value' field
            color: color,
        }];
    };

    // Render function using custom extractors
    const renderTooltip = (props: TooltipProps<number, string>) => {
        // Pass active and payload, plus custom extractors
        return (
            <GraphTooltip
                active={props.active}
                payload={props.payload}
                titleExtractor={pieTitleExtractor}
                itemsExtractor={pieItemsExtractor}
                // Default valueFormatter (Math.round + toLocaleString) is fine
            />
        );
    };

    return (
        <ResponsiveContainer width="100%" height="100%">
            <PieChart>
                <Pie
                    data={validPieData}
                    cx="50%"
                    cy="50%"
                    outerRadius="80%" // Adjust as needed
                    fill="#8884d8" // Default fill, overridden by Cell
                    dataKey="value"
                    nameKey="name"
                    labelLine={false} // Avoid default labels/lines
                    isAnimationActive={false} // Optimization
                >
                    {validPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                </Pie>
                <RechartsTooltip
                    content={renderTooltip}
                    position={{ x: 0, y: 0 }} // Set fixed position
                    offset={10} // Add offset
                    isAnimationActive={false}
                    // allowEscapeViewBox removed
                />
                {/* Optional: <Legend /> */}
            </PieChart>
        </ResponsiveContainer>
    );
}; 